/**
 * Dropdown — floating-anchor menu with keyboard navigation,
 * outside-click and Escape dismissal.
 *
 * Uses @floating-ui/react for anchoring with flip middleware.
 * Consumes only CSS custom-property references (var(--*)) for
 * colour, font, spacing, radius, and focus-ring.
 *
 * Spec: specs/integration-plan.md section 3.6
 * FR: FR-003 (7 foundational primitives)
 */
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  type CSSProperties,
} from "react";
import {
  useFloating,
  flip,
  offset,
  shift,
  autoUpdate,
  type Placement,
} from "@floating-ui/react";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DropdownItem {
  key: string;
  label: string;
  icon?: string;
  onClick?: () => void;
  href?: string;
  /** Renders a horizontal rule above this item */
  divider?: boolean;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: "start" | "end";
  ariaLabel: string;
}

// ---------------------------------------------------------------------------
// Styles (CSS custom-property references only)
// ---------------------------------------------------------------------------

const menuStyle: CSSProperties = {
  borderRadius: "var(--radius-sm)",
  background: "var(--bg1)",
  border: "1px solid var(--color-border)",
  maxWidth: "320px",
  padding: "var(--space-sm)",
  listStyle: "none",
  margin: 0,
  zIndex: 1000,
};

const itemBaseStyle: CSSProperties = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  fontSize: "var(--fs-body)",
  fontFamily: "var(--font-ui)",
  padding: "8px 12px",
  background: "transparent",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  textAlign: "left",
  textDecoration: "none",
  borderRadius: "var(--radius-xs)",
  outline: "none",
};

const itemHoverStyle: CSSProperties = {
  background: "var(--bg2)",
};

const dividerStyle: CSSProperties = {
  border: "none",
  borderTop: "1px solid var(--color-border)",
  margin: "var(--space-xs) 0",
};

const focusRingStyle: CSSProperties = {
  boxShadow: "var(--focus-ring)",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Dropdown({
  trigger,
  items,
  align = "start",
  ariaLabel,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const menuId = useId();
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const triggerRef = useRef<HTMLElement | null>(null);
  // Refs to avoid stale closures in the document-level keydown listener
  const focusIndexRef = useRef(focusIndex);
  focusIndexRef.current = focusIndex;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const placement: Placement = `bottom-${align}`;

  const { refs, floatingStyles, placement: actualPlacement } = useFloating({
    open,
    placement,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Merge trigger ref with floating reference ref
  const setTriggerRef = useCallback(
    (node: HTMLElement | null) => {
      triggerRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  // Focus management
  useEffect(() => {
    if (open && focusIndex >= 0 && focusIndex < items.length) {
      itemRefs.current[focusIndex]?.focus();
    }
  }, [focusIndex, open, items.length]);

  // Reset focus index when menu closes
  useEffect(() => {
    if (!open) {
      setFocusIndex(-1);
      setHoveredIndex(-1);
    }
  }, [open]);

  // Outside-click dismissal, Escape dismissal, and keyboard navigation
  // All handled at document level so key events on the trigger element
  // (which retains focus after click) still reach the menu logic.
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const floating = refs.floating.current;
      const reference = triggerRef.current;

      if (floating && floating.contains(target)) return;
      if (reference && reference.contains(target)) return;

      setOpen(false);
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((prev) => {
          const len = itemRefs.current.length;
          if (len === 0) return -1;
          if (prev < 0) return 0;
          return (prev + 1) % len;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((prev) => {
          const len = itemRefs.current.length;
          if (len === 0) return -1;
          if (prev <= 0) return len - 1;
          return prev - 1;
        });
      } else if (e.key === "Enter") {
        // Only handle Enter when a menu item is focused (focusIndex >= 0).
        // We read focusIndex from the ref-synced value to avoid stale closure.
        const idx = focusIndexRef.current;
        if (idx >= 0 && idx < itemRefs.current.length) {
          e.preventDefault();
          const item = itemsRef.current[idx];
          if (item?.onClick) {
            item.onClick();
          }
          setOpen(false);
        }
      }
    }

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, refs.floating]);

  // Toggle open on trigger click
  const handleTriggerClick = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  // Item click handler
  const handleItemClick = useCallback(
    (item: DropdownItem) => {
      if (item.onClick) {
        item.onClick();
      }
      setOpen(false);
    },
    [],
  );

  // Clone the trigger element to inject aria attributes
  const triggerElement = React.isValidElement(trigger)
    ? React.cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
        onClick: handleTriggerClick,
        "aria-expanded": String(open),
        "aria-haspopup": "true",
        "aria-controls": open ? menuId : undefined,
        ref: setTriggerRef,
      })
    : trigger;

  return (
    <>
      {triggerElement}
      {open && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, ...menuStyle }}
          role="menu"
          id={menuId}
          aria-label={ariaLabel}
          data-placement={actualPlacement}
        >
          {items.map((item, index) => {
            const isFocused = focusIndex === index;
            const isHovered = hoveredIndex === index;

            const combinedStyle: CSSProperties = {
              ...itemBaseStyle,
              ...(isHovered ? itemHoverStyle : {}),
              ...(isFocused ? focusRingStyle : {}),
            };

            const commonProps = {
              role: "menuitem" as const,
              tabIndex: isFocused ? 0 : -1,
              ref: (el: HTMLElement | null) => {
                itemRefs.current[index] = el;
              },
              onMouseEnter: () => setHoveredIndex(index),
              onMouseLeave: () => setHoveredIndex(-1),
              style: combinedStyle,
            };

            return (
              <React.Fragment key={item.key}>
                {item.divider && <hr role="separator" style={dividerStyle} />}
                {item.href ? (
                  <a
                    {...commonProps}
                    href={item.href}
                    onClick={(e) => {
                      // Allow navigation but also close the menu
                      handleItemClick(item);
                    }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <div
                    {...commonProps}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.label}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
}
